import { useMutation, useQueryClient } from '@tanstack/react-query';

import { connectorService } from '@/services/connector-service';
import type { UUID } from '@/utils/uuid';

import { connectorMutationKeys, connectorQueryKeys } from './query-keys';

const STOP_CONFLICT_MESSAGE = 'The run has already finished.';

function mapStopFeatureError(error: unknown): Error {
  const message = error instanceof Error ? error.message : String(error);

  if (message.includes('STOP_CONFLICT')) {
    return new Error(STOP_CONFLICT_MESSAGE);
  }

  return error instanceof Error
    ? error
    : new Error('Failed to stop the run. Please try again.');
}

export function useStopConnectorFeatureInvocation(
  tenantId?: UUID,
  connectorId?: UUID
) {
  const qc = useQueryClient();

  return useMutation({
    mutationKey: connectorMutationKeys.stopConnectorInvocation(
      tenantId,
      connectorId
    ),
    mutationFn: async (payload: {
      featureId: UUID;
      runSequence: number;
      message?: string;
    }): Promise<boolean> => {
      if (!tenantId) throw new Error('Missing tenant id');
      if (!connectorId) throw new Error('Missing connector id');

      try {
        return await connectorService.stopConnectorInvocation(
          tenantId,
          connectorId,
          payload.featureId,
          payload.runSequence,
          payload.message
        );
      } catch (error) {
        throw mapStopFeatureError(error);
      }
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: connectorQueryKeys.currentInvocationStatus(
          tenantId,
          connectorId,
          variables.featureId
        ),
        refetchType: 'all',
      });

      // Also refresh the run-specific status so the run tab updates immediately.
      qc.invalidateQueries({
        queryKey: connectorQueryKeys.invocationStatus(
          tenantId,
          connectorId,
          variables.featureId,
          variables.runSequence
        ),
        refetchType: 'all',
      });

      qc.invalidateQueries({
        queryKey: connectorQueryKeys.invocationStatusesByFeature(
          tenantId,
          connectorId,
          variables.featureId
        ),
        refetchType: 'all',
      });
    },
    onError: (_error, variables) => {
      // Always refresh status on stop failure (e.g. 409 run already finished).
      // This ensures the UI converges to the actual backend state even if stop
      // was rejected, rather than showing a stale "active" status indefinitely.
      qc.invalidateQueries({
        queryKey: connectorQueryKeys.currentInvocationStatus(
          tenantId,
          connectorId,
          variables.featureId
        ),
        refetchType: 'all',
      });

      qc.invalidateQueries({
        queryKey: connectorQueryKeys.invocationStatus(
          tenantId,
          connectorId,
          variables.featureId,
          variables.runSequence
        ),
        refetchType: 'all',
      });
    },
  });
}
